import type { ThemeTypes } from '@/types/themeTypes/ThemeType';

/**
 * Light theme — VoIP Customer Portal.
 * Brand: deep navy + electric red, derived from company logo.
 *  - Navy (#0a3d6b) = trust, infrastructure, banking-grade reliability
 *  - Red  (#ef2b3d) = energy, action, primary call-to-action accent
 *
 * Token names follow Vuetify conventions; semantic groups:
 *   brand → primary/secondary/accent
 *   state → info/success/warning/error
 *   surface → background/surface/surface-bright/surface-light/surface-variant
 *   on-*   → foreground colors that sit on top of the matching surface
 *   light* / dark* → tinted backgrounds and pressed/hover variants
 */
const PurpleTheme: ThemeTypes = {
  name: 'PurpleTheme',
  dark: false,
  variables: {
    'border-color': '#0a3d6b',
    'high-opacity': 1
  },
  colors: {
    // ---- Brand ----
    primary: '#0a3d6b',         // navy — main brand
    secondary: '#1e5a8a',       // mid-blue — secondary brand / gradients
    accent: '#ef2b3d',           // brand red — highlights, KPI accents

    // ---- Semantic state ----
    info: '#0ea5e9',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',

    // ---- Surfaces ----
    background: '#f4f6fa',                  // app background (cool grey-blue)
    surface: '#ffffff',                     // cards
    'surface-bright': '#ffffff',
    'surface-light': '#f8fafc',             // subtle table header
    'surface-variant': '#eef2f7',
    'on-surface': '#0f172a',                // main text
    'on-surface-variant': '#475569',        // secondary text
    'on-primary': '#ffffff',
    'on-secondary': '#ffffff',

    // ---- Tinted backgrounds ----
    lightprimary: '#e6eef6',                 // navy tint (chips, soft buttons)
    lightsecondary: '#e0ebf5',
    lightsuccess: '#d1fae5',
    lighterror: '#fee2e2',
    lightwarning: '#fef3c7',
    lightinfo: '#e0f2fe',
    lightaccent: '#fee2e8',                  // red tint

    // ---- Pressed / hover variants ----
    darkprimary: '#052a4d',                  // darker navy
    darksecondary: '#143f63',
    darkinfo: '#0284c7',
    darksuccess: '#059669',
    darkwarning: '#d97706',
    darkerror: '#dc2626',
    darkaccent: '#c91d2e',                   // pressed red

    // ---- Text ----
    darkText: '#0f172a',
    lightText: '#64748b',

    // ---- Borders & inputs ----
    borderLight: '#e2e8f0',
    inputBorder: '#cbd5e1',
    containerBg: '#f4f6fa',
    'border-opacity': '0.5',

    // ---- Social ----
    facebook: '#1877f2',
    twitter: '#1da1f2',
    linkedin: '#0a66c2',

    // ---- Greys ----
    gray100: '#f8fafc',
    gray200: '#f1f5f9',
    gray300: '#e2e8f0',
    gray400: '#94a3b8',
    gray500: '#64748b',
    gray700: '#334155',
    gray900: '#0f172a',

    primary200: '#9bb6cf',
    secondary200: '#b8d0e3'
  }
};

export { PurpleTheme };
